import React, { useEffect, useRef } from 'react';
import { CheckCircle2, XCircle, Loader2, Clock } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { MailMergeResult } from './mailmergeExport';

interface MailMergeProgressListProps {
  results: Map<string, MailMergeResult>;
}

const MailMergeProgressList: React.FC<MailMergeProgressListProps> = ({ results }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-scroll to currently generating item
    if (scrollRef.current) {
      const generating = scrollRef.current.querySelector('[data-status="generating"]');
      generating?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [results]);

  const entries = Array.from(results.values());

  return (
    <ScrollArea className="h-[300px] rounded-md border border-border/60">
      <div ref={scrollRef} className="p-2 space-y-1">
        {entries.map((r) => (
          <div
            key={r.leadId}
            data-status={r.status}
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm hover:bg-muted/40 transition-colors"
          >
            <StatusIcon status={r.status} />
            <span className="flex-1 truncate font-medium">{r.leadName}</span>
            <span className="text-xs text-muted-foreground truncate max-w-[140px]">
              {typeof r.leadEmail === 'string' ? r.leadEmail || 'No email' : 'No email'}
            </span>
            {r.status === 'failed' && r.error && (
              <span className="text-xs text-destructive truncate max-w-[160px]" title={r.error}>
                {r.error}
              </span>
            )}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

function StatusIcon({ status }: { status: MailMergeResult['status'] }) {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4 text-muted-foreground shrink-0" />;
    case 'generating':
      return <Loader2 className="h-4 w-4 text-primary animate-spin shrink-0" />;
    case 'success':
      return <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-destructive shrink-0" />;
  }
}

export default MailMergeProgressList;
